import { set, child, onValue } from 'firebase/database'
import { proxy, useSnapshot } from 'valtio'

import * as firebaseConnection from '../firebaseConnection'
import { type Floor, type Identifier } from '../model/floor-model'
import { type DeepReadonly } from '../typeUtils'
import { roomState } from '../../webrtc/webRTCRoom'

import {
  type PanelState,
  panelState,
  usePanel,
  usePanelScreen,
} from './usePanel'
import { cardIsOfType, type TrayState, useTrayState } from './useTray'

const ROOT_NODE = 'floor'

const floorState = proxy<{ floor: Floor | null }>({
  floor: null,
})

const floorInitState = proxy<{ isFloorReady: boolean }>({
  isFloorReady: false,
})

// Subscribe
firebaseConnection.runWhenReady((connection) => {
  // Adds a Firebase listener that triggers when the floor changes.
  onValue(child(connection.room, ROOT_NODE), (snapshot) => {
    floorState.floor = snapshot.val()
    floorInitState.isFloorReady = true
  })
})

// Actions

/**
 * Sets the floor.
 * @param floor - The floor to be set.
 */
const setFloor = async (floor: Floor) => {
  const connection = await firebaseConnection.get()

  return set(child(connection.room, ROOT_NODE), floor).catch((err: Error) => {
    connection.logError('SET_FLOOR', 'Failed to set the floor', err)
  })
}

/**
 * Switches to solo mode.
 * @param participantId - The id of the participant.
 * @param identifier - The identifier (camera, video, etc).
 */
const switchToSolo = (participantId: number, identifier: Identifier) =>
  setFloor({
    layout: 'solo',
    participantId,
    identifier,
  })

/**
 * Switches to panel mode.
 * @param participantIds - The ids of the participants on panel or auto if all available should be displayed
 */
const switchToPanel = () => {
  setFloor({
    layout: 'panel',
  })
}

/** Switches to tray layout. */
const switchToTray = () =>
  setFloor({
    layout: 'tray',
  })

/**
 * Switches to mixed mode.
 * @param participantId - The id of the participant.
 * @param identifier - The identifier (camera, video, etc).
 */
const switchToSideBySide = (participantId: number, identifier: Identifier) =>
  setFloor({
    layout: 'side-by-side',
    identifier,
    participantId,
  })

const useFloorState = () => useSnapshot(floorState).floor

const useIsFloorReadyState = () => useSnapshot(floorInitState).isFloorReady

const useIsVideoShowing = () => {
  const { floor } = useSnapshot(floorState)
  const panelScreen = usePanelScreen()
  return (
    (floor?.layout === 'solo' &&
      floor?.identifier === 'camera' &&
      floor?.participantId === 0) ||
    (floor?.layout === 'panel' && panelScreen === 'tray')
  )
}

const isParticipantCameraInFloor = (
  participantId: number,
  floor: Floor | null,
  panel: PanelState
) => {
  if (!floor) return false

  switch (floor.layout) {
    case 'solo':
      return (
        floor.identifier === 'camera' && floor.participantId === participantId
      )
    case 'side-by-side':
      return floor.participantId === participantId
    case 'panel':
      return (
        panel.mode === 'auto' ||
        (panel.mode === 'manual' &&
          panel.participantIds?.includes(participantId))
      )
    case 'tray':
    default:
      return false
  }
}

const isParticipantCameraAloneInFloor = (
  participantId: number,
  floor: Floor | null,
  panel: PanelState
) => {
  if (!floor) return false

  switch (floor.layout) {
    case 'solo':
      return (
        floor.identifier === 'camera' && floor.participantId === participantId
      )
    case 'panel':
      return (
        (panel.mode === 'auto' && roomState.participants.length === 1) ||
        (panel.mode === 'manual' &&
          panel.participantIds?.every((id) => id === participantId))
      )
    case 'side-by-side':
    case 'tray':
    default:
      return false
  }
}

const useIsParticipantCameraInFloor = (participantId: number) => {
  const floor = useFloorState()
  const panel = usePanel()

  return isParticipantCameraInFloor(participantId, floor, panel)
}

const isParticipantScreenInFloor = (
  participantId: number,
  floor: Floor | null,
  panel: PanelState,
  tray: DeepReadonly<TrayState>
) => {
  if (!floor) return false

  switch (floor.layout) {
    case 'solo':
      return (
        floor.identifier === 'screenshare' &&
        floor.participantId === participantId
      )
    case 'side-by-side':
      return (
        floor.identifier === 'screenshare' &&
        floor.participantId === participantId
      )
    case 'panel':
      if (
        participantId === 0 &&
        panel.screen === 'tray' &&
        cardIsOfType(tray.lastActiveMediaCard, 'video')
      ) {
        return true
      }
      return panel.screen === participantId
    case 'tray': {
      return (
        participantId === 0 && cardIsOfType(tray.lastActiveMediaCard, 'video')
      )
    }
    default:
      return false
  }
}

const isParticipantScreenAloneInFloor = (
  participantId: number,
  floor: Floor | null,
  panel: PanelState,
  tray: TrayState
) => {
  if (!floor) return false
  switch (floor.layout) {
    case 'solo':
      return (
        floor.identifier === 'screenshare' &&
        floor.participantId === participantId
      )
    case 'panel':
      if (panel.participantIds.length > 0) return false
      if (
        participantId === 0 &&
        panel.screen === 'tray' &&
        cardIsOfType(tray.lastActiveMediaCard, 'video')
      )
        return true
      return panel.screen === participantId
    case 'tray':
      return (
        participantId === 0 && cardIsOfType(tray.lastActiveMediaCard, 'video')
      )
    case 'side-by-side':
    default:
      return false
  }
}

const useIsParticipantScreenInFloor = (participantId: number) => {
  const floor = useFloorState()
  const panel = usePanel()
  const tray = useTrayState()
  return isParticipantScreenInFloor(participantId, floor, panel, tray)
}

const isParticipantMobileInFloor = (
  participantId: number,
  floor: Floor | null
) => {
  if (!floor) return false

  switch (floor.layout) {
    case 'solo':
      return (
        floor.identifier === 'mobile' && floor.participantId === participantId
      )
    case 'side-by-side':
      return (
        floor.identifier === 'mobile' && floor.participantId === participantId
      )
    case 'panel':
    case 'tray':
    default:
      return false
  }
}

const useIsParticipantMobileInFloor = (participantId: number) => {
  const floor = useFloorState()
  return isParticipantMobileInFloor(participantId, floor)
}

const isFloorValid = (participantsIds: number[]) => {
  const { floor } = floorState
  const panel = panelState
  if (!floor) return false

  switch (floor.layout) {
    case 'solo':
    case 'side-by-side': {
      return participantsIds.includes(floor.participantId)
    }
    case 'panel': {
      if (panel.mode === 'auto') return true
      return panel.participantIds?.some((id) => participantsIds.includes(id))
    }
    default:
      return true
  }
}

export {
  floorState,
  floorInitState,
  useFloorState,
  useIsFloorReadyState,
  switchToSolo,
  switchToSideBySide,
  switchToPanel,
  switchToTray,
  isFloorValid,
  useIsVideoShowing,
  isParticipantCameraInFloor,
  isParticipantScreenInFloor,
  isParticipantMobileInFloor,
  isParticipantCameraAloneInFloor,
  isParticipantScreenAloneInFloor,
  useIsParticipantCameraInFloor,
  useIsParticipantScreenInFloor,
  useIsParticipantMobileInFloor,
}
